import React, { useState, useCallback, useMemo, useEffect, useRef, lazy, Suspense } from 'react';
import { Typography, Pagination, useCheckWidth } from '@milanlaser/mlcomponents';

import { calculateDistance } from './utils';
import { ClinicLocationMapCard } from './ClinicLocationMapCard/ClinicLocationMapCard';
import type { Coordinates, MapLocation } from './Map/Map';
import { useNearbyLocations } from './useNearbyLocations';

// Lazy load the Map component to prevent SSR issues
const Map = lazy(() => import('./Map/Map').then((module) => ({ default: module.Map })));

export interface S32MapViewProps {
    readonly className?: string;
    readonly defaultCoordinates?: Coordinates;
    readonly sortOrigin?: Coordinates;
    readonly enableGeolocation?: boolean;
    readonly locations: MapLocation[];
    readonly currentStateName?: string;
    readonly maxNearbyLocations?: number;
    readonly onLocationSelect?: (location: MapLocation) => void;
    readonly onNearbyLocationsChange?: (locations: MapLocation[]) => void;
    readonly searchPlaceholder?: string;
    readonly title?: string;
}

const maptilerKey = process.env.GATSBY_MAPTILER_API_KEY;
export const DEFAULT_COORDINATES: Coordinates = [42.0, -89.0]; // Default to Illinois

const DEFAULT_ZOOM = 7;
const CLOSE_ZOOM = 12;
const STATE_SEARCH_RADIUS = 500;
const DEFAULT_SEARCH_RADIUS = 100;

// Map loading placeholder component
const MapLoadingPlaceholder: React.FC<{ height: string }> = ({ height }) => (
    <div
        className="w-full flex items-center justify-center bg-gray-100"
        style={{ height, borderRadius: '30px 30px 0 30px / 30px 30px 0px 30px' }}
    >
        <div className="text-center">
            <div className="animate-pulse">
                <svg
                    className="w-12 h-12 mx-auto mb-2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                </svg>
                <Typography tag="p" variant="pSubhead" color="Grey" className="!text-sm">
                    Loading map...
                </Typography>
            </div>
        </div>
    </div>
);

export const S32MapView: React.FC<S32MapViewProps> = ({
    locations = [],
    currentStateName,
    defaultCoordinates,
    sortOrigin: initialSortOrigin,
    searchPlaceholder = 'Enter Zip, City, or State',
    className,
    onLocationSelect,
    onNearbyLocationsChange,
    enableGeolocation = true,
    title = 'Find Your Milan Laser Hair Removal Location',
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [mapCenter, setMapCenter] = useState<Coordinates>(defaultCoordinates || DEFAULT_COORDINATES);
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
    const [autocompleteResults, setAutocompleteResults] = useState<any[]>([]);
    const [isMapReady, setIsMapReady] = useState(false);

    // Use custom hook to manage nearby locations state
    const { nearbyLocations, setNearbyLocationIds, defaultNearbyIds } = useNearbyLocations(
        locations,
        currentStateName,
        onNearbyLocationsChange
    );

    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [searchError, setSearchError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [selectedLocationPage, setSelectedLocationPage] = useState<number | null>(null);
    const [referenceLocation, setReferenceLocation] = useState<Coordinates | null>(null);
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const isProgrammaticPageChangeRef = useRef(false);

    const { isMobile } = useCheckWidth();

    const itemsPerPage = 4;
    const totalPages = Math.ceil(nearbyLocations.length / itemsPerPage);

    // Check if we're in the browser and set map as ready
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMapReady(true);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        const fetchAutocomplete = async () => {
            if (!searchValue.trim()) {
                setAutocompleteResults([]);
                return;
            }

            try {
                const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(searchValue)}.json?key=${maptilerKey}&autocomplete=true&fuzzy=true&country=us&&limit=5`;
                const response = await fetch(url, { signal: controller.signal });
                const data = await response.json();
                setAutocompleteResults(data.features || []);
            } catch (err) {
                console.error('Autocomplete error:', err);
            }
        };

        const timeout = setTimeout(fetchAutocomplete, 300);
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [searchValue]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    // Helper to calculate nearby locations
    const calculateNearbyLocations = useCallback(
        (lat: number, lng: number, radius: number) => {
            return locations
                .map((location) => ({
                    ...location,
                    distance: calculateDistance(lat, lng, location.coords[0], location.coords[1]),
                }))
                .filter((l) => l.distance <= radius)
                .sort((a, b) => a.distance - b.distance);
        },
        [locations]
    );

    // Helper to reset to error state
    const resetToErrorState = useCallback(() => {
        setSearchError(true);
        setNearbyLocationIds(defaultNearbyIds); // Changed
        setMapCenter(DEFAULT_COORDINATES);
        setMapZoom(DEFAULT_ZOOM);
        setReferenceLocation(null);
        setSelectedLocationId(null);
        setCurrentPage(1);
    }, [defaultNearbyIds]); // Changed dependency

    // Reset map view and selection when page changes
    const prevPageRef = useRef(currentPage);
    useEffect(() => {
        if (prevPageRef.current === currentPage) {
            return;
        }

        prevPageRef.current = currentPage;

        if (isProgrammaticPageChangeRef.current) {
            isProgrammaticPageChangeRef.current = false;
            return;
        }

        setSelectedLocationId(null);
        setSelectedLocationPage(null);
        setMapCenter(referenceLocation || defaultCoordinates || DEFAULT_COORDINATES);
        setMapZoom(DEFAULT_ZOOM);
    }, [currentPage, defaultCoordinates, referenceLocation]);

    // Sort the full list for pagination/highlight. Until the user searches or picks a location,
    // sort by distance from `initialSortOrigin` (e.g. the center of the metro) when one is provided.
    const sortOrigin = referenceLocation ?? initialSortOrigin;
    const sortLat = sortOrigin?.[0];
    const sortLng = sortOrigin?.[1];
    const sortedNearbyLocations = useMemo(() => {
        if (
            typeof sortLat !== 'number' ||
            typeof sortLng !== 'number' ||
            !Number.isFinite(sortLat) ||
            !Number.isFinite(sortLng)
        ) {
            return nearbyLocations;
        }
        // A location with bad coordinates sinks to the end instead of breaking the sort. Its real (NaN) distance is
        // kept on the object so the card hides it rather than showing "Infinity mi".
        const sortKey = (distance: number) => (Number.isFinite(distance) ? distance : Infinity);
        return nearbyLocations
            .map((location) => ({
                ...location,
                distance: calculateDistance(sortLat, sortLng, location.coords[0], location.coords[1]),
            }))
            .sort((a, b) => {
                const keyA = sortKey(a.distance);
                const keyB = sortKey(b.distance);
                return keyA === keyB ? 0 : keyA < keyB ? -1 : 1;
            });
    }, [nearbyLocations, sortLat, sortLng]);

    const paginatedLocations = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedNearbyLocations.slice(start, start + itemsPerPage);
    }, [currentPage, sortedNearbyLocations]);

    // Get the current paginated locations for highlighting
    const closestLocations = useMemo(() => {
        const paginatedIds = paginatedLocations.map((location) => location.id);

        // Only include selected location if we're on the page where it was selected
        if (selectedLocationId && !paginatedIds.includes(selectedLocationId) && selectedLocationPage === currentPage) {
            return [selectedLocationId, ...paginatedIds.slice(0, 3)];
        }

        return paginatedIds;
    }, [paginatedLocations, selectedLocationId, selectedLocationPage, currentPage]);

    const handleGeolocation = useCallback(() => {
        if (!enableGeolocation || !navigator.geolocation) {
            return;
        }

        setIsLoadingLocation(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                const userPos: Coordinates = [latitude, longitude];
                setMapCenter(userPos);
                setMapZoom(10);
                setReferenceLocation(userPos);
                setCurrentPage(1);
                setIsLoadingLocation(false);
            },
            (error) => {
                console.error('Error getting location:', error);
                setIsLoadingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000,
            }
        );
    }, [enableGeolocation]);

    const handleSearchFromFeature = useCallback(
        async (feature: any) => {
            const [lng, lat] = feature.center;
            const searchPos: Coordinates = [lat, lng];
            const isStateSearch = feature.place_type.includes('region');
            const searchRadius = isStateSearch ? STATE_SEARCH_RADIUS : DEFAULT_SEARCH_RADIUS;

            const nearby = calculateNearbyLocations(lat, lng, searchRadius);

            if (nearby.length === 0) {
                resetToErrorState();
            } else {
                setSearchError(false);
                setMapCenter(searchPos);
                setMapZoom(isStateSearch ? DEFAULT_ZOOM : CLOSE_ZOOM);
                setNearbyLocationIds(nearby.map((loc) => loc.id)); // Changed
                setReferenceLocation(searchPos);
            }

            setCurrentPage(1);
        },
        [calculateNearbyLocations, resetToErrorState]
    );

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!searchValue.trim()) return;

        try {
            const geocodingUrl = `https://api.maptiler.com/geocoding/${encodeURIComponent(searchValue)}.json?key=${maptilerKey}&country=us&limit=5`;

            const response = await fetch(geocodingUrl);
            const data = await response.json();

            if (data.features && data.features.length > 0) {
                const feature = data.features[0];
                await handleSearchFromFeature(feature);
            } else {
                resetToErrorState();
            }
        } catch (err) {
            console.error('Geocoding error:', err);
            resetToErrorState();
        }
    };

    const handleMapLocationSelect = useCallback(
        (location: MapLocation) => {
            const nearby = calculateNearbyLocations(location.coords[0], location.coords[1], DEFAULT_SEARCH_RADIUS);

            const nearbyIds = nearby.map((loc) => loc.id);
            if (!nearbyIds.includes(location.id)) {
                nearbyIds.unshift(location.id);
            }

            setSelectedLocationId(location.id);
            setSelectedLocationPage(1);
            setMapCenter(location.coords);
            setMapZoom(CLOSE_ZOOM);
            setSearchError(false);
            setNearbyLocationIds(nearbyIds);
            setReferenceLocation(location.coords);

            // Only set the flag if we're actually changing pages
            if (currentPage !== 1) {
                isProgrammaticPageChangeRef.current = true;
            }
            setCurrentPage(1);

            onLocationSelect?.(location);
        },
        [onLocationSelect, calculateNearbyLocations, currentPage]
    );

    const mapHeight = isMobile ? '285px' : '447px';

    return (
        <div className={`flex flex-col max-w-7xl mx-auto !px-4 py-8 gap-12 md:gap-11 ${className || ''}`}>
            <div className="text-center w-full mx-auto md:w-max">
                <Typography tag="h2" variant="h2-large" color="Blue" className="mb-8 md:mb-7">
                    {title}
                </Typography>

                <div className="flex flex-col gap-12 items-center md:gap-8 md:flex-row md:justify-between">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();

                            if (searchDebounceRef.current) {
                                clearTimeout(searchDebounceRef.current);
                            }

                            searchDebounceRef.current = setTimeout(() => {
                                handleSearch(e);
                            }, 300);
                        }}
                        className="w-full max-w-md flex"
                    >
                        <div className="flex flex-col w-full relative">
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={(e) => {
                                    setSearchValue(e.target.value);
                                    if (searchError) setSearchError(false);
                                }}
                                className={`w-full p-2.5 border border-solid border-milan-grey rounded-[10px] outline-none text-milan-black placeholder-black ${searchError ? 'border-2 border-red-500' : ''}`}
                                autoFocus
                            />
                            {autocompleteResults.length > 0 && (
                                <ul className="absolute top-full left-0 right-0 z-[500] bg-white border border-milan-grey rounded-[10px] px-0 mt-1 max-h-60 overflow-y-auto">
                                    {autocompleteResults.map((result) => (
                                        <li
                                            key={result.id}
                                            className="cursor-pointer px-3 py-2 hover:bg-milan-blue-10 text-sm hover:bg-gray-200 !list-none text-left"
                                            onClick={() => {
                                                setAutocompleteResults([]);
                                                setSearchValue('');
                                                handleSearchFromFeature(result);
                                            }}
                                        >
                                            <img
                                                src="/assets/icons/selected-map-pin.svg"
                                                alt=""
                                                className="w-4 h-4 mr-2"
                                            />
                                            <Typography tag="span" className="m-0">
                                                {result.place_name}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {searchError && (
                                <div className="text-red-500 absolute -bottom-9 left-0">
                                    <Typography tag="p" variant="pSubhead" color="Red" className="!text-xs !font-bold">
                                        No results found. Please try again.
                                    </Typography>
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            className="bg-transparent border-none cursor-pointer ml-2 flex items-center justify-center text-milan-blue hover:text-milan-turquoise"
                            aria-label="Search"
                        >
                            <SearchIcon />
                        </button>
                    </form>

                    {enableGeolocation && (
                        <button
                            onClick={handleGeolocation}
                            disabled={isLoadingLocation}
                            className="text-lg flex items-center gap-2 whitespace-nowrap bg-transparent border-none text-milan-blue transition-colors cursor-pointer hover:underline hover:text-milan-turquoise"
                        >
                            <Typography
                                tag="span"
                                variant="p"
                                color="Blue"
                                className="text-lg font-semibold text-inherit"
                            >
                                {isLoadingLocation ? 'Getting Location...' : 'Use My Current Location'}
                            </Typography>
                            <LocationIcon />
                        </button>
                    )}
                </div>
            </div>

            <div
                className="overflow-hidden shadow-md"
                style={{ borderRadius: '30px 30px 0 30px / 30px 30px 0px 30px' }}
            >
                {isMapReady ? (
                    <Suspense fallback={<MapLoadingPlaceholder height={mapHeight} />}>
                        <Map
                            locations={locations}
                            center={mapCenter}
                            zoom={mapZoom}
                            height={mapHeight}
                            onLocationSelect={handleMapLocationSelect}
                            selectedLocationId={selectedLocationId}
                            closestLocationIds={closestLocations}
                            className="w-full"
                        />
                    </Suspense>
                ) : (
                    <MapLoadingPlaceholder height={mapHeight} />
                )}
            </div>

            <div className="space-y-6 min-h-[400px]">
                <div className="flex-col flex gap-[10px] items-center justify-center md:flex-row h-[38px]">
                    <Typography tag="h3" variant="h3-large" color="Blue" className="m-0">
                        Nearby Locations
                    </Typography>
                    <Typography tag="span" variant="p" color="Black" className="!text-xs !font-bold">
                        {sortedNearbyLocations.length} Location{sortedNearbyLocations.length !== 1 ? 's' : ''}
                    </Typography>
                </div>

                <div className="grid !gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4 min-h-54">
                    {paginatedLocations.map((location) => (
                        <ClinicLocationMapCard
                            key={location.id}
                            location={location}
                            showDistance={!!referenceLocation}
                            isSelected={selectedLocationId === location.id}
                            className="w-full"
                            onCardClick={() => handleMapLocationSelect(location)}
                        />
                    ))}
                </div>

                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        onChange={setCurrentPage}
                        totalItems={sortedNearbyLocations.length}
                        itemsPerPage={itemsPerPage}
                        isInfinite={false}
                        variant="numeric"
                        className="mx-auto"
                    />
                )}
            </div>
        </div>
    );
};

const LocationIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 18 18">
        <path
            fill="currentColor"
            d="M3 0a3 3 0 0 0-3 3v1.5a.75.75 0 1 0 1.5 0V3A1.5 1.5 0 0 1 3 1.5h1.5a.75.75 0 0 0 0-1.5zm10.5 0a.75.75 0 1 0 0 1.5H15A1.5 1.5 0 0 1 16.5 3v1.5a.75.75 0 1 0 1.5 0V3a3 3 0 0 0-3-3zM9 5.25a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5M1.5 13.5a.75.75 0 1 0-1.5 0V15a3 3 0 0 0 3 3h1.5a.75.75 0 1 0 0-1.5H3A1.5 1.5 0 0 1 1.5 15zm16.5 0a.75.75 0 1 0-1.5 0V15a1.5 1.5 0 0 1-1.5 1.5h-1.5a.75.75 0 1 0 0 1.5H15a3 3 0 0 0 3-3z"
        ></path>
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
        <path
            fill="currentColor"
            d="M10 2a8 8 0 105.293 14.293l4.707 4.707a1 1 0 001.414-1.414l-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12A6 6 0 0110 4z"
        />
    </svg>
);
